import { Check, Lock, Play, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ScenarioStatus } from "@/lib/scenarios";

// Atomic Skills-mode UI primitives. Mirrors the JSX mockups in
// `design/hifi/scenarios.jsx` lines ~74–185. All Skills surfaces
// build from these so the visual language stays consistent.

type CapBadgeProps = {
  children: React.ReactNode;
  // Solid amber fill (used for "in progress"/"ready" emphasis); the
  // default soft variant is for inline labels.
  solid?: boolean;
  sm?: boolean;
  className?: string;
};

/**
 * Pill-shaped status/label badge in the Skills amber palette. Use
 * for short emphatic labels: "в процессе", "A1 · сценарий", etc.
 */
export function CapBadge({ children, solid = false, sm = false, className }: CapBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold tracking-wide",
        sm ? "px-[7px] py-[2px] text-[10px]" : "px-[9px] py-[3px] text-[11px]",
        className,
      )}
      style={{
        background: solid ? "var(--scen-solid)" : "var(--scen-bg)",
        color: solid ? "#fff" : "var(--scen-fg)",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Star marker for capstone scenarios. Inline-flex so it composes
 * cleanly with badges and titles.
 */
export function CapstoneStar({ size = 12, className }: { size?: number; className?: string }) {
  return <Star className={cn("inline-block fill-current", className)} style={{ width: size, height: size }} />;
}

type ScenStatusDotProps = {
  status: ScenarioStatus;
  className?: string;
};

/**
 * 28px circular indicator. Five visual variants — every status the
 * user can see has a distinct dot so the journal page differentiates
 * at a glance without reading labels. (SOFT_LOCKED was removed; the
 * soft-lock state machine no longer exists.)
 */
export function ScenStatusDot({ status, className }: ScenStatusDotProps) {
  // Size + base layout shared by every variant.
  const baseClass = "flex size-7 shrink-0 items-center justify-center rounded-full";

  if (status === "COMPLETED" || status === "NEEDS_REVIEW") {
    return (
      <div
        className={cn(baseClass, className)}
        style={{ background: "var(--scen-solid)", color: "#fff" }}
        aria-label="completed"
      >
        <Check className="size-3.5" strokeWidth={2.5} />
      </div>
    );
  }
  if (status === "READY_FOR_CHECKPOINT") {
    return (
      <div
        className={cn(baseClass, className)}
        style={{
          background: "var(--scen-solid)",
          color: "#fff",
          // Subtle ring to amplify "this is the moment" — matches
          // the boxShadow trick in the mockup (line ~117).
          boxShadow: "0 0 0 3px var(--scen-bg2)",
        }}
        aria-label="ready for checkpoint"
      >
        <Check className="size-3" strokeWidth={2.5} />
      </div>
    );
  }
  if (status === "IN_PROGRESS") {
    return (
      <div
        className={cn(baseClass, className, "border-2")}
        style={{ background: "var(--scen-bg)", borderColor: "var(--scen-solid)" }}
        aria-label="in progress"
      >
        <div className="size-2 rounded-full" style={{ background: "var(--scen-solid)" }} />
      </div>
    );
  }
  if (status === "AVAILABLE") {
    return (
      <div
        className={cn(baseClass, className, "border")}
        style={{
          background: "var(--surface)",
          borderColor: "var(--scen-solid)",
          borderStyle: "dashed",
          borderWidth: 1.5,
          color: "var(--scen-solid)",
        }}
        aria-label="available"
      >
        <Play className="size-3.5" strokeWidth={2} />
      </div>
    );
  }
  // LOCKED — prereqs not met.
  return (
    <div
      className={cn(baseClass, className, "border")}
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border)",
        color: "var(--text-muted)",
      }}
      aria-label="locked"
    >
      <Lock className="size-3" strokeWidth={1.8} />
    </div>
  );
}
